import React, { Component } from 'react';
import moment from 'moment';
import gql from "graphql-tag";
import { graphql, withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import * as Cookies from "es-cookie";

const GET_ACADEMIC_YEARS = gql`
    query getAcademicYears($id: Int) {
      getAcademicYears(id: $id) {
        id
        academic_year
      }
    }
`;

const GET_CLASS_SUBJECTS_BY_ACADEMIC_YEAR = gql`
    query getClassSubjectsByAcademicYear(
        $academic_year_id: Int!
        $exam_standard_ids: String
        $classIds: String
        $institute_id: Int!
    ) {
        getClassSubjectsByAcademicYear(
            academic_year_id: $academic_year_id
            exam_standard_ids: $exam_standard_ids
            classIds: $classIds
            institute_id: $institute_id
        ) {
            class_id
            checked
            class_name
            subject_ids
            subject_names
        }
    }
`;

class ClassDiaryPreview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            allSubjects: [],
            loading: false
        };
    }

    componentDidMount() {
        this.fetchSubjects();
    }

    componentDidUpdate(prevProps) {
        const prevEntries = prevProps.previewData?.entries || [];
        const currEntries = this.props.previewData?.entries || [];
        
        const prevClassId = prevEntries[0]?.class_id;
        const currClassId = currEntries[0]?.class_id;

        if (prevClassId !== currClassId) {
            this.fetchSubjects();
        }
        
        if (prevProps.getAcademicYearsQuery?.loading && !this.props.getAcademicYearsQuery?.loading) {
            if (this.state.allSubjects.length === 0) {
                this.fetchSubjects();
            }
        }
    }

    fetchSubjects = async () => {
        const { previewData, getAcademicYearsQuery, client } = this.props;
        if (!previewData || !previewData.entries || previewData.entries.length === 0) return;
        
        if (getAcademicYearsQuery?.loading || !getAcademicYearsQuery?.getAcademicYears) return;

        const yearsData = getAcademicYearsQuery.getAcademicYears;
        if (yearsData.length === 0) return;
        const academic_year_id = yearsData[0].id;
        
        const class_id = previewData.entries[0].class_id;
        if (!class_id) return;

        this.setState({ loading: true });

        try {
            const res = await client.query({
                query: GET_CLASS_SUBJECTS_BY_ACADEMIC_YEAR,
                variables: {
                    academic_year_id: parseInt(academic_year_id, 10),
                    exam_standard_ids: null,
                    classIds: String(class_id),
                    institute_id: parseInt(Cookies.get("institutionid"), 10),
                },
                fetchPolicy: "no-cache",
            });

            const apiData = res?.data?.getClassSubjectsByAcademicYear || [];
            if (apiData.length > 0) {
                const parseCsv = (value = "") => String(value).split(",").map((item) => item.trim()).filter(Boolean);
                const isCheckedValue = (value) => {
                    if (value === true || value === 1) return true;
                    const normalized = String(value ?? "").trim().toLowerCase();
                    return normalized === "true" || normalized === "1";
                };

                const subjectIds = parseCsv(apiData[0].subject_ids);
                const subjectNames = parseCsv(apiData[0].subject_names);
                const checkedValues = parseCsv(apiData[0].checked);
                
                const subjects = subjectIds.map((id, index) => ({
                    value: parseInt(id, 10),
                    label: subjectNames[index] || id,
                    checked: checkedValues.length ? isCheckedValue(checkedValues[index]) : false,
                })).filter(subject => subject.checked).map(subject => ({ value: subject.value, label: subject.label }));

                subjects.push({ value: 0, label: "Others" });

                this.setState({ allSubjects: subjects, loading: false });
            } else {
                this.setState({ allSubjects: [], loading: false });
            }
        } catch (err) {
            console.error("Error fetching class subjects:", err);
            this.setState({ allSubjects: [], loading: false });
        }
    }

    getMergedData = () => {
        const { previewData } = this.props;
        const { allSubjects } = this.state;
        const entries = previewData?.entries || [];

        // If we don't have subjects yet, just render the entries
        if (allSubjects.length === 0) {
            return entries.map(e => ({
                subject_name: e.subject_name || 'Others',
                description: e.description || ''
            }));
        }

        // We have all subjects. Map them.
        const merged = allSubjects.map(sub => {
            const entry = entries.find(e => String(e.subject_id) === String(sub.value));
            // Only include 'Others' if there's an actual entry for it
            if (sub.value === 0 && !entry) return null;
            return {
                subject_name: sub.label,
                description: entry ? entry.description : ''
            };
        }).filter(Boolean);

        return merged;
    }

    handlePrint = () => {
        const { previewData, orgProfile } = this.props;
        if (!previewData) return;

        const { classLabel, sectionLabel, date } = previewData;
        const dateLabel = moment.unix(date).format("dddd, DD-MM-YYYY");
        const orgName = orgProfile?.organisation_name || "Institution Name";
        const logoUrl = orgProfile?.logo ? (orgProfile.logo.startsWith("http") ? orgProfile.logo : `https://support.rizee.in/files/${orgProfile.logo}`) : "";
        const orgAddress = orgProfile?.address || "";

        const mergedData = this.getMergedData();

        let entriesHtml = '';
        mergedData.forEach(entry => {
            entriesHtml += `
                <tr>
                    <td class="subject-col">${entry.subject_name}</td>
                    <td class="desc-col">${entry.description || ''}</td>
                </tr>
            `;
        });

        const printWindow = window.open("", "_blank", "width=850,height=1000");
        printWindow.document.write(`
            <html>
            <head>
                <title>Class Diary - ${classLabel} ${sectionLabel}</title>
                <style>
                    body { 
                        font-family: 'Times New Roman', Times, serif; 
                        padding: 40px; 
                        color: #000; 
                        line-height: 1.5; 
                    }
                    .header { 
                        text-align: center; 
                        margin-bottom: 30px; 
                        border-bottom: 2px solid #000; 
                        padding-bottom: 20px; 
                    }
                    .logo { max-height: 80px; margin-bottom: 10px; }
                    .org-title { font-size: 26px; font-weight: bold; text-transform: uppercase; margin-bottom: 5px; }
                    .org-address { font-size: 14px; margin-bottom: 15px; }
                    .report-title { font-size: 20px; font-weight: bold; text-decoration: underline; margin-bottom: 20px; }
                    
                    .meta-info { 
                        display: flex; 
                        justify-content: space-between; 
                        font-size: 16px; 
                        margin-bottom: 20px;
                        font-weight: bold;
                    }
                    
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 50px;
                    }
                    th, td {
                        border: 1px solid #000;
                        padding: 12px;
                        vertical-align: top;
                    }
                    th {
                        background-color: #f0f0f0;
                        font-size: 16px;
                        font-weight: bold;
                        text-align: left;
                    }
                    .subject-col { width: 25%; font-weight: bold; }
                    .desc-col { width: 75%; white-space: pre-wrap; min-height: 40px; }

                    .signatures {
                        display: flex;
                        justify-content: space-between;
                        margin-top: 80px;
                    }
                    .sig-line {
                        width: 250px;
                        border-top: 1px solid #000;
                        text-align: center;
                        padding-top: 5px;
                        font-size: 16px;
                        font-weight: bold;
                    }

                    @media print { 
                        body { padding: 0; } 
                        th { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    ${logoUrl ? `<img src="${logoUrl}" class="logo" />` : ''}
                    <div class="org-title">${orgName}</div>
                    ${orgAddress ? `<div class="org-address">${orgAddress}</div>` : ''}
                    <div class="report-title">STUDENT DIARY</div>
                </div>
                
                <div class="meta-info">
                    <span>Class & Section: ${classLabel} - ${sectionLabel}</span>
                    <span>Date: ${dateLabel}</span>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th>Subject</th>
                            <th>Homework / Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${entriesHtml}
                    </tbody>
                </table>


                <script>
                    window.onload = function() { setTimeout(function(){ window.print(); }, 500); }
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    render() {
        const { previewData } = this.props;
        const { loading } = this.state;

        if (!previewData) {
            return (
                <div style={{ textAlign: "center", padding: "80px 20px" }}>
                    <p style={{ color: "#94a3b8" }}>Please select a class to view its diary preview.</p>
                </div>
            );
        }

        const { classLabel, sectionLabel, date } = previewData;
        const dateLabel = moment.unix(date).format("dddd, DD-MM-YYYY");
        const mergedData = this.getMergedData();

        return (
            <div style={{ background: "#fff", padding: "10px" }}>
                {/* Header Action */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
                    <button 
                        onClick={this.handlePrint}
                        disabled={loading}
                        style={{ 
                            background: "#000", color: "#fff", border: "none", padding: "8px 16px", 
                            borderRadius: "4px", fontWeight: "bold", fontSize: "14px", display: "flex", 
                            alignItems: "center", gap: "8px", cursor: "pointer" 
                        }}
                    >
                        <i className="fas fa-print" /> {loading ? "Loading Subjects..." : "Print Diary"}
                    </button>
                </div>

                {/* Print Layout Preview inside UI */}
                <div style={{ border: "1px solid #ccc", padding: "30px", fontFamily: "'Times New Roman', Times, serif", color: "#000" }}>
                    <div style={{ textAlign: "center", marginBottom: "20px", borderBottom: "2px solid #000", paddingBottom: "10px" }}>
                        <h2 style={{ fontSize: "24px", fontWeight: "bold", textTransform: "uppercase", margin: "0 0 5px 0" }}>Student Diary</h2>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "16px", marginBottom: "20px" }}>
                        <span>Class & Section: {classLabel} - {sectionLabel}</span>
                        <span>Date: {dateLabel}</span>
                    </div>

                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "40px" }}>
                        <thead>
                            <tr>
                                <th style={{ border: "1px solid #000", padding: "10px", background: "#f0f0f0", textAlign: "left", width: "30%" }}>Subject</th>
                                <th style={{ border: "1px solid #000", padding: "10px", background: "#f0f0f0", textAlign: "left" }}>Homework / Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mergedData.map((entry, idx) => (
                                <tr key={idx}>
                                    <td style={{ border: "1px solid #000", padding: "10px", fontWeight: "bold" }}>{entry.subject_name}</td>
                                    <td style={{ border: "1px solid #000", padding: "10px", whiteSpace: "pre-wrap", minHeight: "40px" }}>{entry.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>


                </div>
            </div>
        );
    }
}

export default compose(
    withApollo,
    graphql(GET_ACADEMIC_YEARS, {
        name: "getAcademicYearsQuery",
        options: {
            variables: { id: 1 },
        },
        fetchPolicy: "network-only"
    })
)(ClassDiaryPreview);
